import React from "react"
import Reveal from "./Reveal.jsx"

const INCLUDED = [
  "Unlimited experiments. AI proposes, runs, and retires them around the clock.",
  "Bayesian winner detection. Winners apply automatically at 95% confidence.",
  "Vision-AI shop profiling. Palette, typography, voice, and verified facts learned from your pages.",
  "Per-device targeting & multi-viewport screenshots — mobile, tablet, desktop.",
  "Live traffic + funnel analytics. Click-ID attribution, dark-traffic floor, per-source CVR.",
  "Read-only Shop Chat. Ask why conversion dropped — get a tool-grounded answer.",
  "Hosted snippet on global CDN. Stale-while-origin-offline keeps you online during outages.",
  "Cancel any time. No annual commitment.",
]

function formatDollars(cents) {
  return `$${Math.round(cents / 100)}`
}

export default function Pricing({ priceCents, copy = {}, ctaHref = "/users/sign_up" }) {
  const dollars = formatDollars(priceCents)

  return (
    <section
      id="pricing"
      className="border-t border-[color:var(--color-rule)] bg-[color:var(--color-surface-page)]"
    >
      <div className="max-w-6xl mx-auto px-6 py-32">
        <Reveal>
          <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-[color:var(--color-ai)] mb-4">
            § 05 · Pricing
          </p>
          <h2 className="font-sans text-[40px] tracking-[-0.035em] font-medium leading-[1.1] max-w-2xl">
            30 days free. Then one flat price.
          </h2>
          <p className="mt-4 font-sans text-[15px] text-[color:var(--color-ink-secondary)] max-w-xl leading-[1.6]">
            {copy.intro || "Try every feature for 30 days. Card required at sign-up, but no charge during the trial. Cancel any time and the card is never charged."}
          </p>
        </Reveal>

        <Reveal delay={120}>
          <div className="mt-16 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-px bg-[color:var(--color-rule)] border border-[color:var(--color-rule)] rounded-[var(--radius-card-lg)] overflow-hidden">
            <div className="bg-[color:var(--color-surface-card)] p-12 flex flex-col justify-between gap-10">
              <div>
                <span className="inline-flex items-center gap-2 bg-[color:var(--color-winner-fill)] border border-[color:var(--color-winner)]/20 rounded-[var(--radius-pill)] px-3 py-1.5 self-start mb-6">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-[color:var(--color-winner-dot)]" />
                  <span className="font-mono text-[10px] tracking-[0.12em] uppercase text-[color:var(--color-winner)] font-medium">
                    30-day free trial
                  </span>
                </span>
                <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-[color:var(--color-ink-tertiary)] mb-4">
                  Then · Standard
                </p>
                <div className="flex items-baseline gap-2">
                  <span
                    className="font-sans font-medium text-[color:var(--color-ink-primary)] leading-none"
                    style={{ fontSize: "84px", letterSpacing: "-0.045em", fontVariantNumeric: "tabular-nums" }}
                  >
                    {dollars}
                  </span>
                  <span className="font-sans text-[18px] text-[color:var(--color-ink-tertiary)] tracking-[-0.02em]">
                    /month
                  </span>
                </div>
                <p className="mt-4 font-sans text-[14px] text-[color:var(--color-ink-secondary)] leading-[1.55] max-w-sm">
                  {copy.billing || "Card required at sign-up. No charge during the trial. Cancel any time and you're never billed. After 30 days, $99/month per webshop (excl. VAT where applicable), billed monthly in USD via Stripe."}
                </p>
              </div>

              <a
                href={ctaHref}
                className="inline-flex items-center justify-center bg-[color:var(--color-brand)] text-white font-sans text-[14px] font-medium px-5 py-3.5 rounded-[var(--radius-button)] hover:opacity-90 transition-opacity self-start"
              >
                {copy.cta || "Start your free trial"}
              </a>
            </div>

            <div className="bg-[color:var(--color-surface-card)] p-12">
              <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-[color:var(--color-ink-tertiary)] mb-6">
                Included
              </p>
              <ul className="flex flex-col gap-4">
                {INCLUDED.map((line) => (
                  <li
                    key={line}
                    className="flex items-start gap-3 font-sans text-[14px] text-[color:var(--color-ink-primary)] leading-[1.55]"
                  >
                    <span
                      aria-hidden
                      className="mt-2 inline-block w-1 h-1 rounded-full bg-[color:var(--color-ai)] flex-shrink-0"
                    />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Reveal>

        <Reveal delay={240}>
          <p className="mt-10 font-mono text-[11px] tracking-[0.04em] text-[color:var(--color-ink-faint)]">
            {copy.stores || "One subscription · one webshop · multi-shop pricing arrives in V2."}
          </p>
        </Reveal>
      </div>
    </section>
  )
}
